import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Search, Menu, X, Flame } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/build-bouquet", label: "Build a Bouquet" },
  { to: "/gifts", label: "Occasions" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!search || search.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?search=${encodeURIComponent(search)}`);
        setSuggestions(data.slice(0, 5));
      } catch (e) { console.warn("Search suggestions failed", e); }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
      setSearchOpen(false);
      setSearch("");
    }
  };

  return (
    <>
      <div className="w-full bg-[#1A1A1A] text-[#FDFBF7] text-xs py-2 overflow-hidden" data-testid="top-banner">
        <div className="flex whitespace-nowrap tracking-widest-plus uppercase animate-marquee">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0">
              {[
                "Hand-poured in Howrah",
                "Free shipping on orders above ₹699",
                "5.0 ★ Google Reviews",
                "Made in small batches",
              ].map((t, j) => (
                <span key={j} className="mx-8">✦ {t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
          scrolled ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-[#E3DCD2]" : "bg-[#FDFBF7] border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_scent-studio-94/artifacts/5yodkusi_unnamed.png"
              alt="Ave Luxe Candles"
              className="w-12 h-12 md:w-14 md:h-14 object-contain transition-transform duration-500 group-hover:-rotate-6"
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-serif-display text-xl md:text-2xl tracking-tight">Ave Luxe</div>
              <div className="text-[9px] uppercase tracking-widest-plus text-[#9BA595]">bespoke gifting</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide relative py-1 transition-colors ${
                    isActive ? "text-[#D27D60]" : "text-[#1A1A1A] hover:text-[#D27D60]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-full hover:bg-[#F3EFEA] transition-colors"
              data-testid="header-search-toggle"
              aria-label="Search"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>

            <Link
              to="/cart"
              className="p-2 rounded-full hover:bg-[#F3EFEA] transition-colors relative"
              data-testid="header-cart-link"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-[#D27D60] text-[#FDFBF7] text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center"
                  data-testid="header-cart-count"
                >
                  {count}
                </span>
              )}
            </Link>

            <Sheet>
              <SheetTrigger asChild>
                <button
                  className="md:hidden p-2 rounded-full hover:bg-[#F3EFEA] transition-colors"
                  data-testid="header-mobile-menu-btn"
                  aria-label="Menu"
                >
                  <Menu className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#FDFBF7] w-72 border-l border-[#E3DCD2]">
                <div className="mt-8 flex flex-col gap-6">
                  {NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={({ isActive }) =>
                        `font-serif-display text-2xl ${isActive ? "text-[#D27D60]" : "text-[#1A1A1A]"}`
                      }
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-[#E3DCD2] bg-[#FDFBF7]" data-testid="header-search-panel">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
              <form onSubmit={submitSearch} className="flex items-center gap-3">
                <Search className="w-4 h-4 text-[#9BA595]" strokeWidth={1.5} />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search candles, kits, bouquets..."
                  className="flex-1 bg-transparent outline-none text-sm py-2"
                  data-testid="header-search-input"
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="p-1"
                  data-testid="header-search-close"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </form>
              {suggestions.length > 0 && (
                <div className="mt-3 space-y-2 pb-2">
                  {suggestions.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.slug}`}
                      onClick={() => { setSearchOpen(false); setSearch(""); }}
                      className="flex items-center gap-3 p-2 hover:bg-[#F3EFEA] rounded-md transition-colors"
                      data-testid={`search-suggestion-${p.slug}`}
                    >
                      <img src={p.images[0]} alt={p.name} className="w-10 h-10 object-cover rounded" />
                      <div className="text-sm">{p.name}</div>
                      <div className="ml-auto text-sm text-[#9BA595]">₹{p.price}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
